<script lang="ts">
import { defineComponent, PropType, ref, watch, nextTick } from 'vue'
import { addAutoWrapperOnYoutubeIframe } from '@/utilities/youtube'
import htmlUtil from '@/utilities/html'

export default defineComponent({
  name: 'ExternalHtmlWrapper',

  props: {
    contentsHtml: {
      type: String,
      default: '',
    },

    classNames: {
      type: Array as PropType<string[]>,
      default: () => ['div'],
    },

    /**
     * ページの種類
     * @description 適用される CSS に影響。「contents」指定の場合は contents ページ用の ol スタイルが適用される 等。
     */
    pageType: {
      type: String as PropType<'contents' | 'solution' | 'default'>,
      default: 'default'
    },

    /**
     * 全体的なトンマナをデフォルトではなく独自のカラーにしたい場合の設定
     *
     * @description primary、secondary 双方に「#」から始まる 16 進数のカラーコードを指定
     */
    colorPalette: {
      type: Object as PropType<{
        primary: string,
        secondary: string,
      }>,
      default: () => ({ primary: '', secondary: '' }),
      validator(value: {
        primary: string,
        secondary: string,
      }) {
        // カラーコード未指定か、#RRGGBB の形式で指定されていれば true
        const isValidColor = (value: string) => {
          return value === '' || /^#([0-9a-fA-F]{6})$/.test(value)
        }

        // #RRGGBB の形式か判定
        return isValidColor(value.primary) && isValidColor(value.secondary)
      },
    },

    isCopyBlock: {
      type: Boolean,
      default: false,
    },

    targetBlankForExternalLink: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['rendered'],

  setup(props, { emit }) {
    const root = ref()

    watch(root, () => {
      nextTick(() => {
        if (root.value && root.value.innerHTML) {
          addAutoWrapperOnYoutubeIframe(root.value)

          if (props.isCopyBlock) htmlUtil.preventImageCopy(root.value)
          if (props.targetBlankForExternalLink) htmlUtil.toTargetBlankForExternalLink(root.value)

          emit('rendered', root.value)
        }
      })
    })

    return {
      root,
    }
  }
})
</script>

<template>
  <div
    ref="root"
    :class="classNames"
    data-external-html
    :data-page-type="pageType"
    :style="{
      '--primary-color': colorPalette.primary,
      '--secondary-color': colorPalette.secondary,
    }"
    v-html="contentsHtml" />
</template>

<style lang="sass">
  @import "@/styles/_core/_external.sass"
</style>
